import * as React from 'react';
import autobind from 'autobind-decorator';
import { resolve } from 'inversify-react';
import { observable, computed } from 'mobx';
import { observer } from 'mobx-react';
import * as moment from 'moment';

import { ToastNotifications } from 'src/core/ui/toast';
import { ControllerProps } from 'src/router';
import { ObjectUtils } from 'src/core/utils/object';
import { ConfigurationService } from 'src/core/services/configuration.service';
import { ContextService } from 'src/core/services/context.service';
import { QuoteFormView } from './quote-form.view';
import { QuoteNewForm } from './quote-new-form';
import { QuoteDataService } from './quote-form.data';
import { FormatService } from 'src/core/services/format.service';
import { QuotePaxDataService } from 'src/app/bookings/quote-paxs/quote-pax-form.data';
import { IdentityService } from 'src/core/services/identity.service';
import { QuoteEntryDataService } from 'src/app/bookings/quote-entries/quote-entry-form.data';
import { PolicyCheckingService } from 'src/app/bookings/shared/policy-checking.service';
import { CalculatePriceService } from 'src/app/bookings/shared/calculate-prices.service';
import { ErratasService } from 'src/app/bookings/shared/erratas.service';
import { PaymentListDataService } from 'src/app/accounting/shared/payments/list/payment-list.data';
import { BillingService } from '../../../accounting/shared/billing.service';
import { TaxListDataService } from 'src/app/general/taxes/list/tax-list.data';
import { ConfigOptionsService } from 'src/core/services/config-options.service';
import { CommunicationService } from 'src/core/services/communication.service';

interface Props extends ControllerProps<{ id: string }> {
}

interface State {
	mode: ControlMode;
	defaultLanguage?: string;
	taxes: TaxSummary[];
	billing: BillingItem[];
	requiresDeposit: boolean;
	depositPercent: number;
	dueDateInDays: number;
	currencyCode: string;
	currencyId: number;
	openInitialDeposit: boolean;
}

@observer
export class QuoteFormController extends React.Component<Props, State> {
	@resolve
	configurationService: ConfigurationService;

	@resolve
	configService: ConfigOptionsService;

	@resolve
	contextService: ContextService;

	@resolve
	calculatePriceService: CalculatePriceService;

	@resolve
	formatService: FormatService;

	@resolve
	identityService: IdentityService;

	@resolve
	policyCheckingService: PolicyCheckingService;

	@resolve
	erratasService: ErratasService;

	@resolve
	toast: ToastNotifications;

	@resolve
	billingService: BillingService;

	@observable
	dataService: QuoteDataService;

	@observable
	paxDataService: QuotePaxDataService;

	@observable
	entryDataService: QuoteEntryDataService;

	@observable
	private paymentDataService: PaymentListDataService;

	private permissions = ['Quote', 'Quote_Write', 'Quote_Write_Own'];

	@observable
	autoconnetProductsConfig: { products: AutoConnetProductConfig[] };

	@observable
	useAllotmentBeforeBookings: boolean;

	@resolve
	communicationService: CommunicationService;

	constructor(props: Readonly<Props>) {
		super(props);

		this.state = {
			mode: 'display',
			taxes: [],
			billing: [],
			requiresDeposit: true,
			depositPercent: 0,
			dueDateInDays: 0,
			currencyCode: '',
			currencyId: 0,
			openInitialDeposit: false
		};
	}

	public componentDidMount() {
		this.communicationService.EventEmitter.on("entityChange", this.onEntityChange.bind(this));
		this.initialize(this.props.match.params.id);
	}

	public componentWillReceiveProps(nextProps: Readonly<Props>) {
		if (nextProps.match.params.id !== this.props.match.params.id) {
			this.initialize(nextProps.match.params.id);
		}
	}

	public componentWillUnmount() {
		this.communicationService.EventEmitter.removeListener("entityChange", this.onEntityChange.bind(this));
	}

	async loadAutoconnectProductConfig() {
		this.autoconnetProductsConfig = await this.configService.getAutoConnetProductConfig();
	}

	private onEntityChange = (data: EntityChange) => {
		if (
			data.TypeName == 'Quote' ||
			data.TypeName == 'QuoteEntry' ||
			data.TypeName == 'QuotePax'
		) {
			this.toast.showToast("Reloading", "We receive model changes from server. We are reloading data", "info");
			this.reloadQuote(true);
		}
	}

	@computed
	get title() {
		if (this.dataService && this.dataService.item) {
			const quote = this.dataService.item;
			const client = this.state.billing.find(e => e.id === quote.clientId);
			const margin = quote.totalSaleGross - quote.totalCost;
			return `
				${quote.status === 0 ? "Quote" : "Booking"} ${quote.quoteNumber || ''},
				Client ${client ? client.displayName : ''},
				Price ${quote.totalSaleGross} ${this.state.currencyCode},
				Margin ${ObjectUtils.round(margin)} ${this.state.currencyCode} (${ObjectUtils.round(100 * margin / (quote.totalSaleGross == 0 ? 1 : quote.totalSaleGross))}%)
			`;
		}

		return '';
	}

	@autobind
	onModeChanged(mode: ControlMode) {
		if (!this.permissions.some((r) => this.identityService.isAuthorized(r)) && mode === 'edit') {
			return;
		}

		this.setState({ mode: mode });
	}

	@observable
	rowCss = "";
	@observable
	erratas: ErratasQuoteEntryItem[];
	@observable
	erratasConfirmed: ErratasQuoteEntryItemConfirmed[];
	updateQuote = (items?: QuoteEntryItem[]): void => {
		const quote = this.dataService.item;
		const { taxes, depositPercent, dueDateInDays } = this.state;

		this.dataService.updateQuote(quote, depositPercent, dueDateInDays, taxes, items || this.entryDataService.items, items != null);
	}

	@autobind
	getOccupancy(entry: QuoteEntryItem): PersonOccupancy[] {
		let paxs: QuotePaxItem[] = this.paxDataService.items;
		const quote = this.dataService.items[0];
		return entry.dynamicProperties.occupancy
			.map((e: QuoteEntryOccupancy) => {
				const pax = paxs.find(x => x.id == e.id);
				if (pax == null) {
					return undefined;
				}
				return {
					id: e.id,
					name: pax ? pax.formalName : "",
					age: pax ? moment.utc(entry.dateTo).diff(moment(pax.dynamicProperties['birthDate']), 'year') : 0,
				};
			})
			.filter(e => e != undefined);
	}

	@autobind
	getSearchParameter(entry: QuoteEntryItem, occupancies: Occupancy[]) {
		let api: Api = { apiName: 'ProductBookingSearchService', name: 'Bookem', isActive: true, isActiveForApi: true };
		if (entry.dynamicProperties.productProviderName != 'Bookem') {
			api.name = entry.dynamicProperties.productProviderName;
			api.apiName = entry.dynamicProperties.productProviderApiName;
		}

		return this.calculatePriceService.getSearchParameterFromEntry(entry, api, occupancies, false);
	}

	@autobind
	private create() {
		const quote = this.dataService.add(
			this.identityService.userId,
			this.identityService.defaultDestiny || undefined,
			this.identityService.defaultLanguage || this.configurationService.configuration.defaultLanguage,
			this.identityService.defaultMarket || undefined,
			this.identityService.defaultBrand || undefined
		);
		return quote;
	}

	private changeStatus = (cancel: boolean = false) => async () => {
		const quoteRequirePaymentToOpen = this.state.requiresDeposit;
		const depositPaid = this.paymentDataService.items
			.reduce((prev, curr) => prev + curr.sourceAmount, 0) >= this.dataService.item.deposit;
		const quoteId = this.dataService.item.id;
		if (!quoteRequirePaymentToOpen || depositPaid) {
			const context = await this.contextService.current();
			const result = await context.QuoteChangeStatus(quoteId, cancel);

			if (cancel) {
				this.toast.showToast('Request succeed!', "Booking is cancelled", 'success');
			} else {
				this.toast.showToast('Request succeed!', "Quote is opened now a Booking, requests to products' suppliers can be send now", 'success');
			}
			await this.reloadQuote();
			await this.loadEntries(this.dataService.item.id);
		} else if (!depositPaid) {
			this.setState({ openInitialDeposit: true });
		}
	}

	private initialize(id: string): void {
		this.props.setBusy(true);

		Promise.all([this.loadConfiguration(), this.loadQuote(id), this.loadAutoconnectProductConfig()])
			.catch((error) => {
				this.toast.showToast('Request failed', ObjectUtils.parseError(error), 'error');
			})
			.then(() => {
				this.props.setBusy(false);
			});
	}

	private async performAsync<T>(body: () => Promise<T>): Promise<T> {
		const { setBusy } = this.props;

		setBusy(true);
		try {
			const result = await body();
			return result;
		} catch (error) {
			this.toast.showToast('Request failed', ObjectUtils.parseError(error), 'error');
		} finally {
			setBusy(false);
		}
	}

	private async loadConfiguration(): Promise<void> {
		const appConfig = await this.configurationService.current();
		const taxListDataService = TaxListDataService.make();

		taxListDataService.addFilter('isDeleted', 'eq', false);
		taxListDataService.addSorting('id', 'asc', true);
		taxListDataService.pageSize = 100;
		const taxes = await taxListDataService.load(query => query);
		this.useAllotmentBeforeBookings = await this.configService.getUseAllotmentBeforeBookings();
		this.setState({
			taxes,
			defaultLanguage: appConfig.defaultLanguage
		});
	}

	private async loadQuote(id: string): Promise<void> {
		this.dataService = QuoteDataService.make(this.contextService);
		this.entryDataService = QuoteEntryDataService.make(this.policyCheckingService, this.contextService);
		this.paxDataService = QuotePaxDataService.make(this.contextService);
		this.paymentDataService = PaymentListDataService.make(this.contextService);

		if (id === 'new') {
			// Create new
			await this.dataService.create(this.create);
			this.erratas = [];
			this.onModeChanged('edit');

		} else {
			// Load entity
			const quoteId = parseInt(id);

			this.dataService.addFilter('id', 'eq', quoteId);
			this.dataService.addSorting('id', 'asc', true);
			this.dataService.pageSize = 1;

			const [quote] = await this.dataService.load(query => query);

			await this.loadEntries(quoteId);
			let res = this.entryDataService.items.find(x => x.dynamicProperties.state == 'invalid') == null;
			this.rowCss = (!res) ? 'invalid' : '';

			let request: AllErratasParameterRequest = { bookingDate: this.dataService.item.reservationDate, erratasParameter: [] };
			for (let entry of this.entryDataService.items) {
				if (entry.dynamicProperties.visibility == null) {
					entry.dynamicProperties.visibility = { client: [], agent: [], supplier: [] };
				}
				request.erratasParameter.push({
					serviceDateFrom: entry.dateFrom,
					serviceDateTo: entry.dateTo,
					supplierId: entry.supplierId,
					productId: entry.productId
				});
			}
			this.erratas = await this.erratasService.getErratasForAllQuoteEntries(request);
			if (this.erratas && this.erratas.length > 0) {
				this.erratasConfirmed = this.getErratasForConfirm(this.erratas, this.entryDataService.items);
			} else {
				this.erratasConfirmed = [];
			}

			await this.loadPaxes(quoteId);
			await this.loadPayments(quoteId, quote.paymentMode);
		}
	}

	getErratasForConfirm(erratas: ErratasQuoteEntryItem[], entries: QuoteEntryItem[]) {
		let result = [];
		for (let errata of erratas) {
			if (errata.erratas && errata.erratas.length > 0) {
				let entry = entries.find(q => q.productId == errata.productId && q.supplierId == errata.supplierId);
				if (entry) {
					let confirmed: ErrataItem[] = entry.dynamicProperties.erratasConfirmed;
					if (confirmed && confirmed.length > 0) {
						result = result.concat(confirmed.map(c => ({
							quoteEntryId: entry.id,
							confirmed: true,
							errata: c
						})));
						let notConfirmed = errata.erratas.filter(e => !confirmed.some(c => c.id == e.id));
						result = result.concat(notConfirmed.map(c => ({
							quoteEntryId: entry.id,
							confirmed: false,
							errata: c
						})));
					} else {
						result = result.concat(errata.erratas.map(c => ({
							quoteEntryId: entry.id,
							confirmed: false,
							errata: c
						})));
					}
				}
			}
		}

		return result;
	}

	@computed
	get showModalErratasConfirmation() {
		return this.erratasConfirmed && this.erratasConfirmed.length > 0 && this.erratasConfirmed.some(x => !x.confirmed);
	}

	private loadRelationship<T extends QuoteRelation>(relationshipDataService: DataSet<T>, quoteId: number, pageSize: number, sortBy: keyof T = 'id'): Promise<T[]> {
		relationshipDataService.addFilter('quoteId', 'eq', quoteId);
		relationshipDataService.addSorting(sortBy, 'asc', true);
		relationshipDataService.pageSize = pageSize;

		return relationshipDataService.load(query => query);
	}

	private async loadEntries(quoteId: number): Promise<void> {
		this.entryDataService.addFilter('isDeleted', 'eq', false);
		await this.loadRelationship(this.entryDataService, quoteId, 25, 'order');
	}

	private async loadPaxes(quoteId: number): Promise<void> {
		const items = await this.loadRelationship(this.paxDataService, quoteId, 25, 'order');

		const billing = items
			.filter(p => p.clientId != null)
			.map(o => ({ id: o.clientId, displayName: o.clientDisplayName }));
		if (billing.find(x => x.id == this.dataService.item.clientId) == null) {
			billing.push({ id: this.dataService.item.clientId, displayName: this.dataService.item.clientDisplayName });
		}
		this.setState({
			billing: billing
		});
	}

	private async loadPayments(quoteId: number, paymentMode: number): Promise<void> {
		const accountingTarget = paymentMode === 0 ? 1 : 2;
		this.paymentDataService.addFilter('accountingTarget', 'eq', accountingTarget);
		this.paymentDataService.addFilter('isDeleted', 'eq', false);

		await this.loadRelationship(this.paymentDataService, quoteId, 25);
	}

	@autobind
	async save(): Promise<void> {
		const item = this.dataService.item;
		item.entries = this.entryDataService.items;
		const errors = await item.validate(true);

		if (errors.length > 0) {
			this.toast.showToast('Validation error!', errors.map(e => e.text), 'error');
			return;
		}
		item.entries.forEach((item, i) => {
			if (item.productId <= 0)
				item.productId = undefined;
		});
		item.entries = undefined;
		this.props.setBusy(true);

		try {
			if (this.props.match.params.id === "new") {
				await this.updateFees();
				await this.policyCheckingService.setCommissions(this.dataService.item, this.entryDataService.items.slice());
				this.updateQuote();
			}
			await this.dataService.save();
			if (item.status > 0 && this.entryDataService.items.some(e => e.status <= 3)) {
				this.dataService.reload();
				this.entryDataService.reload();
			}
			this.toast.showDataSaved();
			this.props.history.push('/bookings/quotes/' + this.dataService.item.id);
		} catch (error) {
			this.toast.showToast('Request failed', ObjectUtils.parseError(error), 'error');
		}

		this.props.setBusy(false);
	}

	@autobind
	cancel(): void {
		this.props.history.push('/bookings/quotes');
	}

	@autobind
	private async reloadQuote(forceReload?: boolean): Promise<any> {
		const oldStatus = this.dataService.item.status;
		const result = await this.dataService.reload();
		const newStatus = this.dataService.item.status;
		if (oldStatus != newStatus || forceReload) {
			await this.loadEntries(this.dataService.item.id);
		}

		return result;
	}

	@computed
	get showAlert() {
		if (!!this.entryDataService.items || this.entryDataService.item.isDeleted)
			return false;

		let quoteEntries: QuoteEntryItem[] = this.entryDataService.items;
		let paxs: QuotePaxItem[] = this.paxDataService.items;
		return !paxs.every(m => quoteEntries.some(e => e.dynamicProperties.occupancy.some((x: QuoteEntryOccupancy) => x.id === m.id)));
		//return false;
	}

	@autobind
	async newQuotePax(quote: QuoteItem, client?: ClientItem, dynamicProperties?: any): Promise<QuotePaxItem> {
		if (client != null) {
			if (this.state.billing.find(x => x.id == client.id) == null) {
				this.setState({
					billing: [...this.state.billing, { id: client.id, displayName: client.displayName }]
				});
			}
		}
		let last = this.paxDataService.items.length;
		const item = await this.paxDataService.create(() => this.paxDataService.add(quote, last + 1, client, dynamicProperties));
		item.entity.quote = quote.entity;
		return item;
	}

	@autobind
	private async deletePaxs(paxsDelete: QuotePaxItem[]) {
		if (this.dataService && this.dataService.items.length == 1 && this.entryDataService && this.entryDataService.items && this.entryDataService.items.length > 0) {
			//let quoteEntries: QuoteEntryItem[] = this.entryDataService.items;
			const quote = this.dataService.items[0];
			for (let pax of paxsDelete) {
				for (let entry of this.entryDataService.items) {
					const cost: Money = entry.dynamicProperties.costPriceResult;
					const sell: Money = entry.dynamicProperties.sellPriceResult;
					if (entry.dynamicProperties.driver == pax.id)
						entry.dynamicProperties.driver = undefined;
					delete entry.dynamicProperties.occupancy[pax.id];
					let index = cost.breakdown.findIndex(x => x.paxId == pax.id);
					if (index >= 0) {
						cost.breakdown.splice(index, 1);
					}
					index = sell.breakdown.findIndex(x => x.paxId == pax.id);
					if (index >= 0) {
						sell.breakdown.splice(index, 1);
					}
				}
			}

			let res = await this.updatePrice(null, quote.marketId, quote.reservationDate);
			return res;
		}
	}

	@autobind
	isGenerationEnabled(): boolean { // maybe another check for enabling generation
		return !this.dataService.isDirty;
	}

	updateAgentCommission = async (): Promise<void> => {
		if (this.dataService.item.agentId == null) {
			return;
		}

		const quote = this.dataService.item;
		const entries = this.entryDataService.items;
		await this.policyCheckingService.setCommissions(quote, entries);
	};

	@autobind
	private async updateDependentQuoteEntries(items?: QuoteEntryItem[]): Promise<void> {
		const { setBusy } = this.props;

		items = items || [...this.entryDataService.items];
		setBusy(true);
		await this.entryDataService.deleteDescendantItems(items, false);
		const rootItems = this.entryDataService.items.filter(m => m.productType < 6); // doesn't include SalesAdjustment neither Supplements/Discounts

		const quote = this.dataService.item;
		const paxs = this.paxDataService.items;
		const api: Api = { // just necessary to get parameters
			apiName: 'ProductBookingSearchService',
			name: 'Bookem',
			isActive: true,
			isActiveForApi: true
		};

		const promises = rootItems.map((item) => {
			const occupancy = this.calculatePriceService.getOccupancy(item, paxs);
			const searchParameters = this.calculatePriceService.getSearchParameterFromEntry(item, api, occupancy, false);
			let search: Search & SearchDates;
			if (item.productTypeText === 'Flights') { // this is the only case when 'searchParameters' isn't of type 'SearchDates'
				search = {
					...searchParameters,
					startDate: (searchParameters as SearchFlight).flights[0].startDate,
					endDate: (searchParameters as SearchFlight).flights[0].endDate
				};
			} else {
				search = (searchParameters as Search & SearchDates);
			}

			return this.entryDataService.computeDependencies(quote, item, search);
		});

		await Promise.all(promises);
		setBusy(false);
	}

	@autobind
	private async updateFees(): Promise<void> {
		const quote = this.dataService.item;
		const clients = this.paxDataService.items.slice();

		this.props.setBusy(true);
		try {
			const searchResults = await this.policyCheckingService.getFees(quote, clients);
			const search = {
				startDate: new Date(quote.reservationDate),
				endDate: new Date(quote.reservationDate),
				occupancies: [{
					unit: 1,
					paxs: clients.map(e => ({ id: e.id }))
				}]
			};

			const items = searchResults
				.map(m => m.items)
				.reduce((prev, curr) => [...prev, ...curr], []);

			// Remove old fees
			const deprecatedFees = this.entryDataService.items
				.filter(m => m.dynamicProperties['isFee']);

			for (let i = 0; i < deprecatedFees.length;) {
				const index = items.findIndex(m => m.product.internalId === deprecatedFees[i].productId);
				if (index === -1) {
					i++;
					continue;
				}

				deprecatedFees.splice(i, 1);
				items.splice(index, 1);
			}

			await this.entryDataService.delete(deprecatedFees);

			await this.updateDependentQuoteEntries();

			// TODO: Better handling for errata call api
			const promises = items.map((searchResult) => {
				const selection = this.entryDataService.convertResultToSelection(searchResult);
				return this.entryDataService.add(quote, selection, search as any)
					.then((items) => {
						for (const item of items) {
							item.dynamicProperties['isFee'] = true;
							this.erratasService.erratasProduct(item.productId, quote.reservationDate, item.dateFrom, item.dateTo, item.supplierId)
								.then(errata => {
									let errataduplicate = this.erratas.find(x => x.productId == errata.productId && x.supplierId == errata.supplierId);
									if (errataduplicate == null) {
										this.erratas.push(errata);
										if (errata.erratas && errata.erratas.length > 0)
											for (let er of errata.erratas) {
												if (this.erratasConfirmed == null)
													this.erratasConfirmed = [];
												this.erratasConfirmed.push({
													quoteEntryId: item.id,
													confirmed: false,
													errata: er
												});
											}
									}
								});
						}
						this.entryDataService.updateOrders();
					})
			});

			const messages = searchResults
				.map(m => m.messages)
				.reduce((prev, curr) => [...prev, ...curr], []);
			for (const message of messages) {
				const severity = message.severity === 0
					? 'info'
					: message.severity === 1
						? 'warning'
						: 'error';
				this.toast.showToast(message.text, undefined, severity);
			}

			await Promise.all(promises);
		} catch (error) {
			this.toast.showToast('Error updating the fees', ObjectUtils.parseError(error), 'error');
		} finally {
			this.props.setBusy(false);

			this.updateQuote();
		}
	}

	@autobind
	private async updatePrice(quoteEntries?: QuoteEntryItem[], newMarketId?: number, newBookingDate?: Date): Promise<boolean> {
		try {
			if (this.dataService == null || this.dataService.items == null || this.dataService.items.length > 1 || this.entryDataService == null) {
				return false;
			}

			quoteEntries = (quoteEntries || this.entryDataService.items.slice().filter(m => !m.dynamicProperties.isProductFee))
				.filter(x => (x.productId != null || x.externalId != null) && x.productType !== 6 && x.productType !== 7); // excludes SupplementDiscount(6) and SalesAdjustment(7)
			if (quoteEntries.length === 0) {
				return false;
			}

			this.props.setBusy(true);
			this.rowCss = 'valid';

			const quote = this.dataService.item;
			const paxes = this.paxDataService.items;
			const marketId = newMarketId ? newMarketId : quote.marketId;
			const bookingDate = newBookingDate ? newBookingDate : quote.reservationDate;
			const languageCode = quote.languageCode;

			const newResults: {
				oldVersion: QuoteEntryItem,
				saleAdjustment: QuoteEntryItem,
				resultSelection: ResultSelection,
				search: Search,
			}[] = [];
			const lostSupplementsDiscounts: QuoteEntryItem[] = [];
			for (let entry of quoteEntries) {
				const supplementsDiscounts = this.entryDataService.items.filter(m => m.productType === 6 && m.parentEntryId === entry.id);
				const saleAdjustment = this.entryDataService.items.find(m => m.productType === 7 && m.parentEntryId === entry.id);
				const result = await this.calculatePriceService.getUpdatedEntry(entry, supplementsDiscounts, paxes, marketId, bookingDate, languageCode, false);
				if (result == null) {
					entry.dynamicProperties.state = 'invalid';
				} else {
					newResults.push({
						oldVersion: entry,
						saleAdjustment: saleAdjustment,
						resultSelection: result.resultSelection,
						search: result.search
					});
					lostSupplementsDiscounts.push(...result.lostSupplementsDiscounts);
				}
			}

			const obsoleteEntries = newResults.map(m => m.oldVersion);
			await this.entryDataService.delete(obsoleteEntries);
			await Promise.all(newResults.map(async (m) => {
				const [newEntry] = await this.entryDataService.add(quote, [m.resultSelection], m.search, null, m.oldVersion.dynamicProperties.taxId);
				// Restore important fields of older
				newEntry.parentEntryId = m.oldVersion.parentEntryId;
				newEntry.dynamicProperties.isFee = m.oldVersion.dynamicProperties.isFee;
				newEntry.dynamicProperties.isProductFee = m.oldVersion.dynamicProperties.isProductFee;
				if (newEntry.productType == 2) {
					newEntry.dynamicProperties.driver = m.oldVersion.dynamicProperties.driver;
					newEntry.dynamicProperties.officePickupId = m.oldVersion.dynamicProperties.officePickupId;
					newEntry.dynamicProperties.officeDropOffId = m.oldVersion.dynamicProperties.officeDropOffId;
				}
				if (m.saleAdjustment != null) {
					newEntry.dynamicProperties.hasSaleAdjustment = true;
					newEntry.dynamicProperties.adjustmentType = m.oldVersion.dynamicProperties.adjustmentType;
					if (m.oldVersion.dynamicProperties.adjustmentType === 'relative') {
						newEntry.costPrice = newEntry.dynamicProperties.calculatedCostPrice + m.saleAdjustment.costPrice;
					} else {
						newEntry.costPrice = m.saleAdjustment.costPrice;
						newEntry.salePriceGross = m.saleAdjustment.salePriceGross;
					}
					newEntry.salesmanProfit = m.saleAdjustment.salesmanProfit;

					m.saleAdjustment.quote = this.dataService.item;
					m.saleAdjustment.parentEntryId = newEntry.id;
					await this.entryDataService.cloneItem(m.saleAdjustment);
				}
			}));

			await this.updateAgentCommission();
			this.updateQuote();

			const existInvalidEntry = this.entryDataService.items
				.some(m => m.dynamicProperties.state === 'invalid');

			this.props.setBusy(false);

			if (lostSupplementsDiscounts.length > 0) {
				const msgs = lostSupplementsDiscounts
					.map(m => m.productDisplayName);
				this.toast.showToast('Lost Supplements/Discounts', msgs, 'info');
			}
			if (existInvalidEntry) {
				this.rowCss = 'invalid';
				return false;
			}

			return true;
		} catch (error) {
			this.toast.showToast('Error in search request!', ObjectUtils.parseError(error), 'error');
		}
	}

	@autobind
	private async handlePaymentModeChange(paymentMode: number): Promise<void> {
		this.performAsync(async () => {
			await this.loadPayments(this.dataService.item.id, paymentMode);
		});
	}

	@autobind
	private async handleEditQuotePaxField(quotePax: QuotePaxItem, requireUpdatePrice: boolean = false, requirePolicyChecking: boolean = false): Promise<void> {
		let quoteEntries = this.entryDataService.items.filter(x => (x.productId != null || x.externalId != null) && x.dynamicProperties.occupancy.some(pax => pax.id == quotePax.id));
		if (requireUpdatePrice) {
			await this.updatePrice(quoteEntries);
		} else if (requirePolicyChecking) {
			await this.updateDependentQuoteEntries(quoteEntries);
		}
	}

	render(): React.ReactNode {
		if (this.dataService == null || this.dataService.item == null || this.erratas == null) {
			return null;
		}

		if (this.dataService.item.dynamicProperties['notes'] == null) {
			this.dataService.item.dynamicProperties['notes'] = [];
		}

		if (this.props.match.params.id === 'new') {
			const adults: QuotePaxItem[] = [];
			const children: QuotePaxItem[] = [];
			const infants: QuotePaxItem[] = [];
			for (const pax of this.paxDataService.items.slice()) {
				const age = moment.utc().diff(pax.dynamicProperties.birthDate, "year");
				if (age > 12) {
					adults.push(pax);
				} else if (age > 2) {
					children.push(pax);
				} else {
					infants.push(pax);
				}
			}

			return (
				<QuoteNewForm
					model={this.dataService.item}
					setBusy={this.props.setBusy}
					adults={adults}
					children={children}
					infants={infants}
					mode={this.state.mode}
					onModeChanged={this.onModeChanged}
					cancel={this.cancel}
					save={this.save}
					onPaxCountChange={this.onPaxCountChange}
					onClientChange={this.onClientChange}
					onMarketChange={this.afterMarketLoad}
					onAgentChange={this.onAgentChange}
					contextService={this.contextService}
					formatService={this.formatService}
					defaultLanguage={this.state.defaultLanguage}
				/>
			);
		}

		return (
			<QuoteFormView
				erratasConfirmed={this.erratasConfirmed}
				showModalErratasConfirmation={this.showModalErratasConfirmation}
				model={this.dataService.item}
				identityService={this.identityService}
				formatService={this.formatService}
				entryDataService={this.entryDataService}
				paxDataService={this.paxDataService}
				paymentDataSet={this.paymentDataService}
				generationEnabled={this.isGenerationEnabled()}
				taxes={this.state.taxes}
				billing={this.state.billing}
				newQuotePax={this.newQuotePax}
				title={this.title}
				mode={this.state.mode}
				onModeChanged={this.onModeChanged}
				cancel={this.cancel}
				save={this.save}
				contextService={this.contextService}
				setBusy={this.props.setBusy}
				defaultLanguage={this.state.defaultLanguage}
				changeStatus={this.changeStatus}
				reloadQuote={this.reloadQuote}
				rowCss={this.rowCss}
				getOccupancy={this.getOccupancy}
				getSearchParameter={this.getSearchParameter}
				deletePaxs={this.deletePaxs}
				showAlert={this.showAlert}
				updateAgentCommissions={this.updateAgentCommission}
				updateDependentQuoteEntries={this.updateDependentQuoteEntries}
				updateFees={this.updateFees}
				updatePrice={this.updatePrice}
				updateQuote={this.updateQuote}
				handleEditQuotePaxField={this.handleEditQuotePaxField}
				erratas={this.erratas}
				erratasService={this.erratasService}
				depositPercent={this.state.depositPercent}
				onMarketChange={this.afterMarketLoad}
				dueDateInDays={this.state.dueDateInDays}
				currencyCode={this.state.currencyCode}
				currencyId={this.state.currencyId}
				openInitialDeposit={this.state.openInitialDeposit}
				onSubmitPayment={this.handleSubmitPayment}
				closeInitialDepositDialog={this.closeInitialDepositDialog}
				onAgentChange={this.onAgentChange}
				onPaymentModeChange={this.handlePaymentModeChange}
				autoconnetProductsConfig={this.autoconnetProductsConfig}
				useAllotmentBeforeBookings={this.useAllotmentBeforeBookings}
			/>
		);
	}

	private onPaxCountChange = (type: 'Adult' | 'Children' | 'Infant', count: number) => {
		const paxsToDelete = this.paxDataService.items.filter(pax => {
			if (pax.clientId != null) {
				return false;
			}
			const age = moment.utc().diff(pax.dynamicProperties.birthDate, 'year');
			switch (type) {
				case 'Adult':
					return age > 12;
				case 'Children':
					return age > 2 && age <= 12;
				case 'Infant':
					return age > 0 && age <= 2;
			}
		});
		this.paxDataService.delete(paxsToDelete);
		let total = type === 'Adult' ? count - 1 : count;
		for (let i = 0; i < total; i++) {
			const paxAge = type === 'Adult'
				? 30
				: type === 'Children'
					? 10
					: 1;
			let dynamicProperties = {
				salutation: type === 'Adult' ? 'Mr.' : '',
				foreName: type,
				middleName: '',
				surName: (i + 1).toString(),
				birthDate: moment.utc().subtract(paxAge, 'year').toDate(),
				gender: 'Male',
				billingTo: this.dataService.item.clientId,
				notes: [],
			};
			let last = this.paxDataService.items.length;
			const item = this.paxDataService.createSync(() => this.paxDataService.add(this.dataService.item, last + 1, null, dynamicProperties));
			item.entity.quote = this.dataService.item.entity;
		}
	}

	private onClientChange = async (client: ClientItem) => {
		const last = this.paxDataService.items.length;
		const quote = this.dataService.item;
		const oldClient = this.paxDataService.items.filter(e => e.clientId !== null);
		this.paxDataService.delete(oldClient);
		for (const pax of this.paxDataService.items.slice()) {
			pax.dynamicProperties.billingTo = this.dataService.item.clientId;
		}
		const item = await this.paxDataService.create(() => this.paxDataService.add(quote, last + 1, client));
		item.entity.quote = quote.entity;
	}

	private afterMarketLoad = (selected: MarketSummary): void => {
		if (selected) {
			this.setState({
				requiresDeposit: selected.requiresPayment,
				depositPercent: selected.requiresPayment ? selected.depositPercent : 0,
				dueDateInDays: selected.invoiceDueDateInDays,
				currencyCode: selected.currencyDisplayName,
				currencyId: selected.currencyId
			});
		}
	};

	@autobind
	private async handleSubmitPayment(paymentInfo: PaymentInfo, immediatelyPerformPayment: boolean): Promise<void> {
		try {
			await this.billingService.registerPayment([], paymentInfo, immediatelyPerformPayment);
		} catch (err) {
			this.toast.showToast("Request failed!", ObjectUtils.parseError(err), "error");
		}
		await this.loadPayments(this.dataService.item.id, this.dataService.item.paymentMode);

		const { paymentMode } = this.dataService.item;
		await this.reloadQuote();
		this.dataService.item.paymentMode = paymentMode; // TODO: it's a patch because reloadQuote() method overwrite unsaved properties
	}

	@autobind
	private closeInitialDepositDialog(): void {
		this.setState({ openInitialDeposit: false });
	}

	private onAgentChange = async (agent: AgentSummary): Promise<void> => {
		this.props.setBusy(true);
		
		if (agent) {
			if (this.dataService.item.agentId == null) {
				this.dataService.item.paymentMode = 1;
			}
			this.dataService.item.agentId = agent.id;
			this.dataService.item.agentName = agent.name;
		} else {
			this.dataService.item.agentId = undefined;
			this.dataService.item.agentName = undefined;
		}
		if (this.props.match.params.id !== 'new') {
			await this.policyCheckingService.setCommissions(this.dataService.item, this.entryDataService.items.slice());
		}

		this.props.setBusy(false);
	}
}

interface QuoteRelation { id: number | string; quoteId?: number; }



// WEBPACK FOOTER //
// ./src/app/bookings/quotes/form/quote-form.controller.tsx